import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Modal } from 'antd';
import { MailOutlined, LockOutlined, SafetyOutlined, KeyOutlined } from '@ant-design/icons';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { userAPI } from '../services/api';
import { getApiUrl } from '../utils/apiUrl';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [forgetModalVisible, setForgetModalVisible] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [form] = Form.useForm();
  const [resetForm] = Form.useForm();
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect') || '/dashboard';
  const [bgImage, setBgImage] = useState('');
  const [bgLoaded, setBgLoaded] = useState(false);

  // 处理图片URL，相对路径转为绝对路径
  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // 避免双斜杠问题
    const base = getApiUrl('');
    if (base.endsWith('/') && url.startsWith('/')) {
      return base + url.substring(1);
    }
    return `${base}${url}`;
  };

  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem('lang') || 'en');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // 获取登录页背景图配置
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(getApiUrl('/api/homepage-config'));
        const data = await response.json();
        if (data.code === 0 && data.data.login_background) {
          // 预加载图片避免闪烁
          const img = new Image();
          img.onload = () => {
            setBgImage(data.data.login_background);
            // 延迟一点再显示，确保平滑过渡
            setTimeout(() => setBgLoaded(true), 50);
          };
          img.src = getFullImageUrl(data.data.login_background);
        }
      } catch (error) {
        console.log('获取登录背景配置失败:', error);
      }
    };
    fetchConfig();
  }, []);

  const t = (zh, en) => (lang === 'zh' ? zh : en);

  const [loginError, setLoginError] = useState('');

  const onFinish = async (values) => {
    setLoginError('');
    setLoading(true);
    try {
      const response = await userAPI.login(values.email, values.password);
      if (response.code === 0 && response.data) {
        localStorage.setItem('user_token', response.data.token);
        message.success(t('登录成功', 'Login successful'));
        window.location.href = redirectUrl;
      } else {
        const errMsg = response.message || response.msg || response.error || t('登录失败', 'Login failed');
        setLoginError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
      }
    } catch (error) {
      let errMsg = '';
      const d = error?.response?.data || error;
      if (typeof d === 'string') errMsg = d;
      else errMsg = d?.message || d?.msg || d?.error || t('登录失败', 'Login failed');
      errMsg = String(errMsg);
      setLoginError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const sendResetCode = async () => {
    const email = resetForm.getFieldValue('email');
    if (!email) {
      message.error(t('请输入邮箱', 'Please enter email'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      message.error(t('请输入有效的邮箱地址', 'Please enter a valid email'));
      return;
    }
    
    setSendingCode(true);
    try {
      const response = await fetch(getApiUrl('/api/auth/send-code'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, type: 'reset_password' }),
      });
      const data = await response.json();
      if (data.code === 0) {
        message.success(t('验证码已发送到邮箱', 'Verification code sent'));
        form.setFieldValue('countdown', 60);
      } else {
        message.error(data.message || t('发送失败', 'Send failed'));
      }
    } catch (error) {
      message.error(t('发送失败，请稍后重试', 'Send failed'));
    } finally {
      setSendingCode(false);
    }
  };

  const handleResetPassword = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error(t('两次输入的密码不一致', 'Passwords do not match'));
      return;
    }
    
    setResetLoading(true);
    try {
      const response = await fetch(getApiUrl('/api/auth/reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: values.email,
          code: values.code,
          new_password: values.newPassword,
        }),
      });
      const data = await response.json();
      if (data.code === 0) {
        message.success(t('密码重置成功，请使用新密码登录', 'Password reset successful'));
        setForgetModalVisible(false);
        resetForm.resetFields();
      } else {
        message.error(data.message || t('重置失败', 'Reset failed'));
      }
    } catch (error) {
      message.error(t('重置失败，请稍后重试', 'Reset failed'));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {bgImage && (
        <div 
          className={`auth-bg-layer ${bgLoaded ? 'loaded' : ''}`}
          style={{
            backgroundImage: `url(${getFullImageUrl(bgImage)})`,
          }}
        />
      )}
      <div className="auth-card">
        <div className="auth-logo">
          <img src={require('../utils/img/logo.png')} alt="Blue API" className="auth-logo-icon" />
          <h1>Blue API</h1>
          <p>{t('一个接口，连接所有 AI', 'One API, Connect All AI')}</p>
        </div>

        <Form name="login" onFinish={onFinish} autoComplete="off" size="large">
          <Form.Item
            name="email"
            rules={[
              { required: true, message: t('请输入邮箱', 'Please enter email') },
              { type: 'email', message: t('请输入有效的邮箱地址', 'Please enter a valid email') },
            ]}
          >
            <Input
              prefix={<MailOutlined style={{ color: '#9ca3af', fontSize: 16 }} />}
              placeholder={t('邮箱地址', 'Email address')}
              style={{ height: 44, borderRadius: 8 }}
            />
          </Form.Item>

          <Form.Item name="password" rules={[{ required: true, message: t('请输入密码', 'Please enter password') }]}>
            <Input.Password
              prefix={<LockOutlined style={{ color: '#9ca3af', fontSize: 16 }} />}
              placeholder={t('密码', 'Password')}
              style={{ height: 44, borderRadius: 8 }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" style={{ width: 16, height: 16 }} />
                <span style={{ fontSize: 14, color: '#6b7280' }}>{t('记住我', 'Remember me')}</span>
              </label>
              <a 
                onClick={() => setForgetModalVisible(true)}
                style={{ fontSize: 14, color: '#1677ff', cursor: 'pointer' }}
              >
                {t('忘记密码？', 'Forgot password?')}
              </a>
            </div>
          </Form.Item>

          {loginError && (
            <div style={{ background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#cf1322', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>⚠</span> {loginError}
            </div>
          )}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={{
                height: 44,
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 500,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
              }}
            >
              {t('登 录', 'Login')}
            </Button>
          </Form.Item>
        </Form>

        <div className="auth-footer" style={{ textAlign: 'center' }}>
          {t('还没有账号？', "Don't have an account?")} <Link to="/register">{t('立即注册', 'Register now')}</Link>
          <span style={{ margin: '0 12px', color: '#d9d9d9' }}>|</span>
          <Link to="/">{t('返回官网', 'Back to Home')}</Link>
        </div>
      </div>

      {/* 忘记密码弹窗 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <KeyOutlined style={{ color: '#667eea' }} />
            <span>{t('找回密码', 'Reset Password')}</span>
          </div>
        }
        open={forgetModalVisible}
        onCancel={() => {
          setForgetModalVisible(false);
          resetForm.resetFields();
        }}
        footer={null}
        width={420}
        centered
      >
        <div style={{ marginBottom: 20, padding: '12px 16px', background: '#f6f7ff', borderRadius: 8, fontSize: 14, color: '#666' }}>
          {t('请输入注册时使用的邮箱，我们会发送验证码到您的邮箱', 'Enter the email used during registration, we will send a verification code')}
        </div>

        <Form
          form={resetForm}
          onFinish={handleResetPassword}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="email"
            label={<span style={{ fontWeight: 500 }}>{t('邮箱地址', 'Email')}</span>}
            rules={[
              { required: true, message: t('请输入邮箱', 'Please enter email') },
              { type: 'email', message: t('请输入有效的邮箱', 'Please enter a valid email') },
            ]}
          >
            <Input
              prefix={<MailOutlined style={{ color: '#9ca3af' }} />}
              placeholder={t('请输入注册邮箱', 'Enter registered email')}
              style={{ borderRadius: 8 }}
            />
          </Form.Item>

          <Form.Item
            name="code"
            label={<span style={{ fontWeight: 500 }}>{t('验证码', 'Verification Code')}</span>}
            rules={[
              { required: true, message: t('请输入验证码', 'Please enter code') },
              { len: 6, message: t('验证码为6位', 'Code must be 6 digits') },
            ]}
          >
            <div style={{ display: 'flex', gap: 8 }}>
              <Input
                prefix={<SafetyOutlined style={{ color: '#9ca3af' }} />}
                placeholder={t('邮箱验证码', 'Email code')}
                maxLength={6}
                style={{ flex: 1, borderRadius: 8 }}
              />
              <Button
                onClick={sendResetCode}
                loading={sendingCode}
                style={{ borderRadius: 8, minWidth: 100 }}
              >
                {t('获取验证码', 'Get Code')}
              </Button>
            </div>
          </Form.Item>

          <Form.Item
            name="newPassword"
            label={<span style={{ fontWeight: 500 }}>{t('新密码', 'New Password')}</span>}
            rules={[
              { required: true, message: t('请输入新密码', 'Please enter new password') },
              { min: 8, message: t('密码至少8位', 'Password must be at least 8 characters') },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
              placeholder={t('设置新密码（至少8位）', 'Set new password (at least 8 chars)')}
              style={{ borderRadius: 8 }}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label={<span style={{ fontWeight: 500 }}>{t('确认密码', 'Confirm Password')}</span>}
            dependencies={['newPassword']}
            rules={[
              { required: true, message: t('请确认密码', 'Please confirm') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('两次密码不一致', 'Passwords do not match')));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
              placeholder={t('再次输入新密码', 'Confirm new password')}
              style={{ borderRadius: 8 }}
            />
          </Form.Item>

          <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
            <Button
              block
              onClick={() => {
                setForgetModalVisible(false);
                resetForm.resetFields();
              }}
              style={{ height: 44, borderRadius: 8 }}
            >
              {t('取消', 'Cancel')}
            </Button>
            <Button
              type="primary"
              block
              htmlType="submit"
              loading={resetLoading}
              style={{
                height: 44,
                borderRadius: 8,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
              }}
            >
              {t('重置密码', 'Reset Password')}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Login;
